import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { RealtimeContext } from './realtimeContext';
import { REALTIME_DOMAINS, WS_TICKET_PATH } from './constants';
import {
  buildPerformCommand,
  buildSubscribeCommand,
  buildUnsubscribeCommand,
  extractTicketExpiresSeconds,
  extractTicketFromApiPayload,
  getActionCableWebSocketUrl,
  splitCableFrames,
} from './actionCable';
import { normalizeCableBroadcast } from './normalizeEvent';

export function RealtimeProvider({ children, enabled = true }) {
  const [status, setStatus] = useState('idle');
  const handlersRef = useRef(new Map());
  const subscriptionsRef = useRef(new Set());
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const ticketRefreshTimerRef = useRef(null);
  const backoffMsRef = useRef(1000);
  const intentionalRefreshRef = useRef(false);

  const dispatch = useCallback((event) => {
    const wildcard = handlersRef.current.get(REALTIME_DOMAINS.WILDCARD);
    const domainHandlers = handlersRef.current.get(event.domain);
    const invoke = (set) => {
      if (!set) return;
      set.forEach((fn) => {
        try {
          fn(event);
        } catch {
          /* handler errors stay isolated */
        }
      });
    };
    invoke(wildcard);
    invoke(domainHandlers);
  }, []);

  const flushSubscriptions = useCallback((ws) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    subscriptionsRef.current.forEach((identifierString) => {
      ws.send(buildSubscribeCommand(identifierString));
    });
  }, []);

  const registerDomainHandler = useCallback((domain, handler) => {
    if (!handlersRef.current.has(domain)) {
      handlersRef.current.set(domain, new Set());
    }
    handlersRef.current.get(domain).add(handler);
    return () => {
      handlersRef.current.get(domain)?.delete(handler);
    };
  }, []);

  const subscribe = useCallback((identifierObj) => {
    const identifierString = JSON.stringify(identifierObj);
    subscriptionsRef.current.add(identifierString);
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(buildSubscribeCommand(identifierString));
    }
    return () => {
      subscriptionsRef.current.delete(identifierString);
      const open = wsRef.current;
      if (open && open.readyState === WebSocket.OPEN) {
        open.send(buildUnsubscribeCommand(identifierString));
      }
    };
  }, []);

  const perform = useCallback((identifierObj, action, payload = {}) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    const identifierString = JSON.stringify(identifierObj);
    ws.send(buildPerformCommand(identifierString, action, payload));
    return true;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('disabled');
      return undefined;
    }

    let cancelled = false;

    function clearTimers() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (ticketRefreshTimerRef.current) {
        clearTimeout(ticketRefreshTimerRef.current);
        ticketRefreshTimerRef.current = null;
      }
    }

    function scheduleReconnect() {
      if (cancelled) return;
      clearTimers();
      const delay = backoffMsRef.current;
      setStatus('reconnecting');
      reconnectTimerRef.current = setTimeout(() => {
        backoffMsRef.current = Math.min(backoffMsRef.current * 2, 30000);
        connect();
      }, delay);
    }

    function handleIncomingText(raw) {
      const lines = splitCableFrames(raw);
      for (const line of lines) {
        let msg;
        try {
          msg = JSON.parse(line);
        } catch {
          continue;
        }

        if (msg.type === 'ping') {
          dispatch({
            domain: REALTIME_DOMAINS.SYSTEM,
            type: 'ping',
            payload: msg.message,
          });
          continue;
        }

        if (msg.type === 'welcome') {
          flushSubscriptions(wsRef.current);
          continue;
        }

        if (msg.identifier != null && msg.message !== undefined) {
          const event = normalizeCableBroadcast(msg.identifier, msg.message);
          if (event) dispatch(event);
        }
      }
    }

    async function connect() {
      if (cancelled) return;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      setStatus('fetching_ticket');
      try {
        const { data } = await axios.post(WS_TICKET_PATH);
        const ticket = extractTicketFromApiPayload(data);
        extractTicketExpiresSeconds(data);
        if (cancelled) return;
        if (!ticket) {
          setStatus('ticket_missing');
          scheduleReconnect();
          return;
        }

        setStatus('connecting');
        const url = getActionCableWebSocketUrl(ticket);
        const ws = new WebSocket(url);
        if (cancelled) {
          ws.close();
          return;
        }
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) return;
          backoffMsRef.current = 1000;
          setStatus('open');
        };

        ws.onmessage = (ev) => {
          if (typeof ev.data === 'string') handleIncomingText(ev.data);
        };

        ws.onerror = () => {
          if (!cancelled) setStatus('error');
        };

        ws.onclose = () => {
          clearTimers();
          if (cancelled) return;
          if (wsRef.current === ws) wsRef.current = null;
          setStatus('closed');
          if (intentionalRefreshRef.current) {
            intentionalRefreshRef.current = false;
            backoffMsRef.current = 1000;
            connect();
            return;
          }
          scheduleReconnect();
        };
      } catch {
        if (!cancelled) {
          setStatus('error');
          scheduleReconnect();
        }
      }
    }

    connect();

    return () => {
      cancelled = true;
      clearTimers();
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      }
    };
  }, [dispatch, enabled, flushSubscriptions]);

  const value = useMemo(
    () => ({
      status,
      subscribe,
      perform,
      registerDomainHandler,
    }),
    [perform, registerDomainHandler, status, subscribe],
  );

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}
